import { calculatePulseFromTimestamp } from "components/util/Geometry";
import OBCSpinner from "components/util/OBC";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    updateSperryFilters,
    SET_SPERRY_MARKERS_VISIBILITY,
    SET_SPERRY_MARKERS,
    fetchInspectionSperryFaults,
    requestPlaylistTimestamp,
    focusSperryViewerSearchField,
} from "redux/actions";
import { Input, Select, Button } from "antd";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Highlighter from "react-highlight-words";

const faultsSelector = (state) => state.railInspection.sperryFaults.faults;
const sessionIDSelector = (state) => state.playlist.data.routeID;
const inspectionPulseCountsSelector = (state) => state.railInspection.railInspectionPulseData;
const sperryFiltersSelector = (state) => state.railInspection.sperryViewerFilters;
const sperryMarkersVisibilitySelector = (state) => state.railInspection.sperryMarkersVisibility;

const UTUFaultNavigation = ({ inResizeDraggable }) => {
    const dispatch = useDispatch();
    const { Option } = Select;

    const faults = useSelector(faultsSelector);
    const sessionID = useSelector(sessionIDSelector);
    const inspectionPulseCounts = useSelector(inspectionPulseCountsSelector);
    const [loading, setLoading] = useState(false);
    const sperryFilters = useSelector(sperryFiltersSelector);
    const sperryVisibile = useSelector(sperryMarkersVisibilitySelector);

    const handleMarkerVisibility = () => {
        dispatch({
            type: SET_SPERRY_MARKERS_VISIBILITY,
            sperryMarkersVisibility: !sperryVisibile,
        });
    };

    const handleFilterSearch = (search) => {
        dispatch(updateSperryFilters(search, sperryFilters.priority, sperryFilters.suspectType));
    };

    const handleFilterPriority = (priority) => {
        dispatch(updateSperryFilters(sperryFilters.search, priority, sperryFilters.suspectType));
    };

    const handleFilterType = (type) => {
        dispatch(updateSperryFilters(sperryFilters.search, sperryFilters.priority, type));
    };

    const handleSperryViewerSearchFocus = (focused) => {
        dispatch(focusSperryViewerSearchField(focused));
    };

    const filteredFaults = useMemo(() => {
        return faults.filter((fault) => {
            if (sperryFilters.priority || sperryFilters.priority === 0) {
                if (sperryFilters.priority !== fault.priority) {
                    return false;
                }
            }

            if (sperryFilters.suspectType && sperryFilters.suspectType !== fault.type) {
                return false;
            }

            if (sperryFilters.search && !fault.uid.toString().includes(sperryFilters.search)) {
                return false;
            }

            return true;
        });
    }, [faults, sperryFilters]);

    useEffect(() => {
        const sperryMarkers = faults.map((fault) => {
            const { timestamp, interpolationRatio } = calculatePulseFromTimestamp(fault.pulse_count, inspectionPulseCounts);
            return {
                timestamp,
                interpolationRatio,
                uid: fault.uid,
            };
        });
        dispatch({
            type: SET_SPERRY_MARKERS,
            sperryMarkers: sperryMarkers,
        });
    }, [faults, dispatch, inspectionPulseCounts]);

    useEffect(() => {
        if (!faults.length) {
            setLoading(true);
            dispatch(fetchInspectionSperryFaults(sessionID)).then(() => {
                setLoading(false);
            });
        }
    }, [dispatch, faults.length, sessionID]);

    const goToFault = useCallback(
        (fault) => {
            const timestamp = fault.image_timestamp;

            if (timestamp) {
                dispatch(requestPlaylistTimestamp(timestamp / 1000));
            }
        },
        [dispatch],
    );

    const faultRows = useMemo(() => {
        return filteredFaults.map((fault) => {
            return (
                <div
                    className="InspectRailFaultRow"
                    onClick={() => goToFault(fault)}
                    key={fault.id}>
                    <span className="FaultType">{fault.type}</span>
                    <span className="FaultComment">{fault.comment}</span>
                    <div className="Row">
                        <span className="FaultPriority">
                            <b>Priority:</b> {fault.priority}
                        </span>
                        <span className="FaultRail">
                            <b>Rail:</b> {fault.rail}
                        </span>
                        <span className="FaultRail">
                            <b>Pulse count:</b> {fault.pulse_count}
                        </span>
                        <span className="FaultRail">
                            <b>UID:</b>
                            <Highlighter
                                highlightClassName="ObcTextHighlight"
                                searchWords={String(sperryFilters.search).split(" ")}
                                autoEscape={true}
                                textToHighlight={String(fault.uid)}
                            />
                        </span>
                    </div>
                </div>
            );
        });
    }, [filteredFaults, goToFault, sperryFilters.search]);

    const renderFilters = useMemo(() => {
        if (faults && faults.length) {
            return (
                <div className="FaultListFilters">
                    <div className="FaultListContainer">
                        <span className="FaultListLabel">Priority:</span>
                        <Select
                            className="FaultListSelect"
                            onChange={(e) => handleFilterPriority(e)}
                            placeholder="Select priority level"
                            style={{ width: "100px" }}>
                            <Option value={null}>All</Option>
                            {_.chain(faults)
                                .uniqBy((fault) => fault.priority)
                                .orderBy("priority")
                                .map((fault) => {
                                    return (
                                        <Option
                                            value={fault.priority}
                                            key={fault.id}>
                                            {fault.priority}
                                        </Option>
                                    );
                                })
                                .value()}
                        </Select>
                    </div>
                    <div className="FaultListContainer">
                        <span className="FaultListLabel">Suspect Type:</span>
                        <Select
                            className="FaultListSelect"
                            onChange={(e) => handleFilterType(e)}
                            placeholder="Select suspect type"
                            style={{ width: "200px" }}
                            dropdownMatchSelectWidth={false}>
                            <Option value={null}>All</Option>
                            {_.map(
                                _.uniqBy(faults, (o) => o.type),
                                (fault) => {
                                    return (
                                        <Option
                                            value={fault.type}
                                            key={fault.id}>
                                            {fault.type}
                                        </Option>
                                    );
                                },
                            )}
                        </Select>
                    </div>
                    <div className="FaultListContainer">
                        <span className="FaultListLabel">Search:</span>
                        <Input
                            style={{ width: "150px" }}
                            value={sperryFilters.search}
                            onFocusCapture={() => handleSperryViewerSearchFocus(true)}
                            onBlur={() => handleSperryViewerSearchFocus(false)}
                            onChange={(e) => handleFilterSearch(e.target.value)}
                            placeholder="Search for UID"
                        />
                    </div>
                    <div className="FaultListContainer">
                        <span className="FaultListLabel">Marker visibility</span>
                        {sperryVisibile ? (
                            <Button
                                type="primary"
                                onClick={handleMarkerVisibility}>
                                <FontAwesomeIcon icon={faEye} />
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                onClick={() => handleMarkerVisibility()}>
                                <FontAwesomeIcon icon={faEyeSlash} />
                            </Button>
                        )}
                    </div>
                </div>
            );
        }
        return null;
    }, [faults, sperryFilters, sperryVisibile]);

    return (
        <div
            className="UTUFaultListContainer"
            style={{ minHeight: inResizeDraggable ? "none" : "initial" }}>
            {renderFilters}
            {loading && (
                <div className="Spinner">
                    <OBCSpinner
                        size={70}
                        colorScheme="mono"
                    />
                </div>
            )}
            {!!filteredFaults.length && !loading && (
                <div
                    className="InspectRailFaultList"
                    style={{ maxHeight: inResizeDraggable ? "1000px" : "initial" }}>
                    {faultRows}
                </div>
            )}
            {!!faults.length && !filteredFaults.length && <div className="FaultListFilteredMessage">All results filtered</div>}
            {!faults.length && !loading && <div>No suspects found for this session</div>}
        </div>
    );
};

export default UTUFaultNavigation;
